import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Title, Text, Button, Table, Modal, TextInput, Group } from '@mantine/core'
import {
	createOrgLocation,
	deleteOrgLocation,
	getOrgLocations,
	updateOrgLocation,
} from '../services/actions/orgActions'

const ManageLocationsPage = () => {
	const { orgSlug } = useParams()
	const [locations, setLocations] = useState([])
	const [error, setError] = useState('')
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [newLocationName, setNewLocationName] = useState('')
	const [editingLocation, setEditingLocation] = useState(null)
	const [editingName, setEditingName] = useState('')
	const [deletingLocation, setDeletingLocation] = useState(null)

	const sortedLocations = useMemo(
		() =>
			(locations || [])
				.slice()
				.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
		[locations],
	)

	const loadLocations = async () => {
		try {
			setError('')
			const data = await getOrgLocations(orgSlug)
			setLocations(Array.isArray(data) ? data : [])
		} catch (err) {
			console.error('Failed to load locations:', err)
			setError(err?.message || 'Failed to load locations.')
		}
	}

	useEffect(() => {
		if (!orgSlug) return
		loadLocations()
	}, [orgSlug])

	const handleCreateLocation = async (e) => {
		e.preventDefault()
		try {
			setError('')
			await createOrgLocation(orgSlug, newLocationName)
			setShowCreateModal(false)
			setNewLocationName('')
			await loadLocations()
		} catch (err) {
			console.error('Failed to create location:', err)
			setError(err?.message || 'Failed to create location.')
		}
	}

	const openEditModal = (location) => {
		setEditingLocation(location)
		setEditingName(location?.name || '')
		setShowEditModal(true)
	}

	const handleUpdateLocation = async (e) => {
		e.preventDefault()
		if (!editingLocation) return
		try {
			setError('')
			await updateOrgLocation(orgSlug, editingLocation.id, editingName)
			setShowEditModal(false)
			setEditingLocation(null)
			setEditingName('')
			await loadLocations()
		} catch (err) {
			console.error('Failed to update location:', err)
			setError(err?.message || 'Failed to update location.')
		}
	}

	const openDeleteModal = (location) => {
		setDeletingLocation(location)
		setShowDeleteModal(true)
	}

	const handleDeleteLocation = async () => {
		if (!deletingLocation) return
		try {
			setError('')
			await deleteOrgLocation(orgSlug, deletingLocation.id)
			setShowDeleteModal(false)
			setDeletingLocation(null)
			await loadLocations()
		} catch (err) {
			console.error('Failed to delete location:', err)
			setError(err?.message || 'Failed to delete location.')
		}
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='md'>
				Manage Locations
			</Title>

			{error && (
				<Text color='red' mb='md'>
					{error}
				</Text>
			)}

			<Button mb='lg' onClick={() => setShowCreateModal(true)}>
				➕ Add Location
			</Button>

			{sortedLocations.length > 0 ? (
				<Table withBorder striped highlightOnHover>
					<thead>
						<tr>
							<th>Name</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{sortedLocations.map((location) => (
							<tr key={location.id}>
								<td>{location.name}</td>
								<td style={{ textAlign: 'right' }}>
									<Group spacing='xs' position='right'>
										<Button
											size='xs'
											variant='light'
											onClick={() => openEditModal(location)}
										>
											Edit
										</Button>
										<Button
											size='xs'
											variant='light'
											color='red'
											onClick={() => openDeleteModal(location)}
										>
											Delete
										</Button>
									</Group>
								</td>
							</tr>
						))}
					</tbody>
				</Table>
			) : (
				<Text>No locations found.</Text>
			)}

			<Modal
				opened={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				title='Add Location'
				centered
			>
				<form onSubmit={handleCreateLocation}>
					<TextInput
						label='Location Name'
						value={newLocationName}
						onChange={(e) => setNewLocationName(e.target.value)}
						required
						mb='sm'
					/>
					<Group mt='lg' position='right'>
						<Button variant='light' onClick={() => setShowCreateModal(false)}>
							Cancel
						</Button>
						<Button type='submit'>Save</Button>
					</Group>
				</form>
			</Modal>

			<Modal
				opened={showEditModal}
				onClose={() => setShowEditModal(false)}
				title='Edit Location'
				centered
			>
				<form onSubmit={handleUpdateLocation}>
					<TextInput
						label='Location Name'
						value={editingName}
						onChange={(e) => setEditingName(e.target.value)}
						required
						mb='sm'
					/>
					<Group mt='lg' position='right'>
						<Button variant='light' onClick={() => setShowEditModal(false)}>
							Cancel
						</Button>
						<Button type='submit'>Save</Button>
					</Group>
				</form>
			</Modal>

			<Modal
				opened={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				title='Confirm Delete'
				centered
			>
				<Text mb='md'>
					Delete location <strong>{deletingLocation?.name}</strong>?
				</Text>
				<Group position='right'>
					<Button variant='light' onClick={() => setShowDeleteModal(false)}>
						Cancel
					</Button>
					<Button color='red' onClick={handleDeleteLocation}>
						Delete
					</Button>
				</Group>
			</Modal>
		</Box>
	)
}

export default ManageLocationsPage
